"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
Object.defineProperty(exports, "__esModule", { value: true });
exports.BasePlayer = void 0;
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const AbstractBaseEngine_1 = require("../engines/AbstractBaseEngine");
const Bitmovin_1 = require("../engines/Bitmovin");
const DashJs_1 = require("../engines/DashJs");
const HlsJs_1 = require("../engines/HlsJs");
const interfaces_1 = require("../engines/interfaces");
const Native_1 = require("../engines/Native");
const Shaka_1 = require("../engines/Shaka");
const defaults_1 = require("../utils/defaults");
const helpers_1 = require("../utils/helpers");
const preferences_1 = require("../utils/preferences");
const AbstractPlayer_1 = require("./AbstractPlayer");
const LOWEST_VOLUME = 0.01;
const HIGHEST_VOLUME = 1;
class BasePlayer extends AbstractPlayer_1.AbstractPlayer {
    constructor(videoElement, subtitleContainer, instanceSettings, source, initialState = {}) {
        super(instanceSettings, Object.assign(Object.assign({}, initialState), { isMuted: instanceSettings.initOptions.muted, volume: videoElement.volume }));
        this.videoElement = videoElement;
        this.subtitleContainer = subtitleContainer;
        this.source = source;
        this.scrubChange = 0;
        this.isFakePaused = false;
        this.debouncedSeek = false;
        this.destroyed = false;
        this.setupDebounceSeekTo();
        this.playerEngine = this.getPlayerEngine();
        const { muted } = this.instanceSettings.initOptions;
        if (muted !== undefined) {
            this.setMuted(muted);
        }
        this.setupPlayerEngineEventListeners();
    }
    setupPlayerEngineEventListeners() {
        this.playerEngine.on(interfaces_1.EngineEvents.DRM_UPDATE, this.onDrmUpdate.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.LOADED, this.onLoaded.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.PLAY, this.onPlay.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.PLAYING, this.onPlaying.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.PAUSE, this.onPause.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.TIME_UPDATE, this.onTimeUpdate.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.SEEKING, this.onSeeking.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.SEEKED, this.onSeeked.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.BUFFERING, this.onBuffering.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.BUFFERED, this.onBuffered.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.DROPPED_FRAMES, this.onDroppedFrames.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.SUBTITLE_CHANGED, this.onSubtitleChanged.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.AUDIO_CHANGED, this.onAudioChanged.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.VOLUME_CHANGE, this.onVolumeChanged.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.BITRATE_CHANGED, this.onBitrateChanged.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.ENDED, this.onEnded.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.ERROR, this.onError.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.STATE_CHANGED, this.onEngineStateChange.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.TRACKS_CHANGED, this.onTracksChanged.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.SUBTITLE_CUE_CHANGED, this.onSubtitleCueChanged.bind(this));
        this.playerEngine.on(interfaces_1.EngineEvents.METADATA_EVENT, this.onMetadataChanged.bind(this));
    }
    /**
     * Get the preferred audio & text langauges for media playback.
     * Uses the stored user preferences.
     */
    getPreferredLanguages() {
        const { audio, subtitle } = (0, preferences_1.getPreferences)();
        return { audio, subtitle };
    }
    load(startTime) {
        if (!this.mediaLocator) {
            return Promise.reject(new js_player_shared_1.InitError(`Missing properties: playerEngine: ${this.playerEngine} or mediaLocator: ${this.mediaLocator}`, { type: js_player_shared_1.ErrorTypes.GENERIC }));
        }
        this.startTime = startTime;
        const languages = this.getPreferredLanguages();
        this.playerEngine.load({
            src: this.mediaLocator,
            license: this.license,
            startTime,
            audio: languages.audio,
            subtitle: languages.subtitle,
        });
    }
    /**
     * Bitmovin is the default engine when a license key was provided at init.
     * Without a key, callers keep the stream-type engine selection.
     */
    createBitmovinEngine() {
        var _a;
        const key = (_a = this.instanceSettings.initOptions.bitmovinPlayerKey) === null || _a === void 0 ? void 0 : _a.trim();
        if (!key) {
            return undefined;
        }
        return new Bitmovin_1.Bitmovin(this.videoElement, this.instanceSettings);
    }
    /**
     * ONLY AVAILABLE DURING DEVELOPMENT
     * `getPlayerEngine()` calls this method if the property is set, allows
     * developers to select which engine they want to use for a particular session.
     */
    getPlayerEngineDev() {
        var _a;
        if (process.env.NODE_ENV === "development") {
            if ((_a = window.__RED_BEE_MEDIA__) === null || _a === void 0 ? void 0 : _a.engine) {
                switch (window.__RED_BEE_MEDIA__.engine) {
                    case "shaka":
                        return new Shaka_1.Shaka(this.videoElement, this.instanceSettings);
                    case "dashjs":
                        return new DashJs_1.DashJs(this.videoElement, this.instanceSettings);
                    case "hlsjs":
                        return new HlsJs_1.HlsJs(this.videoElement, this.instanceSettings);
                    case "native":
                        return new Native_1.Native(this.videoElement, this.instanceSettings);
                    case "bitmovin":
                        return new Bitmovin_1.Bitmovin(this.videoElement, this.instanceSettings);
                }
            }
        }
    }
    getPlayerEngine() {
        var _a, _b, _c, _d;
        this.mediaLocator = this.source;
        if (process.env.NODE_ENV === "development") {
            const devEngine = this.getPlayerEngineDev();
            if (devEngine) {
                return devEngine;
            }
        }
        if ((_a = this.mediaLocator) === null || _a === void 0 ? void 0 : _a.includes(".m3u8")) {
            this.playbackFormat = "HLS";
        }
        else if ((_b = this.mediaLocator) === null || _b === void 0 ? void 0 : _b.includes(".mpd")) {
            this.playbackFormat = "DASH";
        }
        else if (((_c = this.mediaLocator) === null || _c === void 0 ? void 0 : _c.includes("ism")) ||
            ((_d = this.mediaLocator) === null || _d === void 0 ? void 0 : _d.includes("Manifest"))) {
            this.playbackFormat = "SMOOTHSTREAMING";
        }
        const bitmovinEngine = this.createBitmovinEngine();
        if (bitmovinEngine) {
            return bitmovinEngine;
        }
        if (this.playbackFormat === "HLS") {
            if ((0, helpers_1.isNativeHlsSupported)()) {
                return new Native_1.Native(this.videoElement, this.instanceSettings);
            }
            return new HlsJs_1.HlsJs(this.videoElement, this.instanceSettings);
        }
        if (this.playbackFormat === "DASH") {
            return new Shaka_1.Shaka(this.videoElement, this.instanceSettings);
        }
        if (this.playbackFormat === "SMOOTHSTREAMING") {
            return new DashJs_1.DashJs(this.videoElement, this.instanceSettings);
        }
        return new Native_1.Native(this.videoElement, this.instanceSettings);
    }
    getPlayerEngineName() {
        return this.playerEngine.name;
    }
    getPlayerEngineVersion() {
        return this.playerEngine.playertechVersion;
    }
    getPlaybackFormat() {
        return this.playbackFormat;
    }
    isLive() {
        return this.playerEngine ? this.playerEngine.isLive() : super.isLive();
    }
    isSeekable() {
        var _a;
        const seekable = (_a = this.playerEngine.getSeekable()) !== null && _a !== void 0 ? _a : { start: 0, end: 0 };
        return this.playerEngine.isLive()
            ? seekable.end - seekable.start > defaults_1.MIN_DVR_WINDOW
            : seekable.end - seekable.start > 0;
    }
    play() {
        if (this.playerEngine && this.state.playbackState !== js_player_shared_1.PlaybackState.IDLE) {
            return this.playerEngine.play();
        }
    }
    pause() {
        this.playerEngine.pause();
    }
    stop() {
        this.playerEngine.stop();
        this.emit(js_player_shared_1.PlayerEvents.STOP, this.getDefaultPlayerEvent());
    }
    setupDebounceSeekTo() {
        if (!this.debounceSeekTo) {
            this.debounceSeekTo = (0, js_player_shared_1.debounce)((time) => {
                this.scrubChange = 0;
                this.debouncedSeek = true;
                this.seekTo(time);
                if (this.isFakePaused) {
                    this.play();
                    this.isFakePaused = false;
                }
            }, 500);
        }
    }
    scrub(change) {
        if (!this.isSeekable()) {
            return;
        }
        const seekable = this.getSeekable();
        const currentTime = this.getCurrentTime();
        const duration = this.getDuration();
        this.scrubChange += change;
        const time = (0, js_player_shared_1.clamp)(currentTime + this.scrubChange, seekable.start, duration);
        // Set the playbackState manually to Seeking thus overriding the engine playbackState
        this.setState({ playbackState: js_player_shared_1.PlaybackState.SEEKING });
        this.onSeeking();
        this.debounceSeekTo(time);
        this.onSeekTimeChange(time);
        if (this.isPlaying()) {
            if (!this.isFakePaused) {
                this.isFakePaused = true;
                this.pause();
            }
        }
    }
    seekTo(time) {
        if (!this.isSeekable()) {
            return;
        }
        this.playerEngine.seekTo(time);
    }
    seekToLive() {
        if (this.playerEngine.isAtLiveEdge()) {
            return;
        }
        return this.playerEngine.seekToLive();
    }
    setPlaybackRate(rate) {
        return this.playerEngine.setPlaybackRate(rate);
    }
    setVolume({ percentage, change }) {
        let volume = 1;
        if (percentage && !change) {
            volume = (0, js_player_shared_1.clamp)(percentage / 100, LOWEST_VOLUME, HIGHEST_VOLUME);
        }
        if (!percentage && change) {
            const currentVolume = this.playerEngine.getVolume() * 100;
            const newVolume = currentVolume + change;
            volume = (0, js_player_shared_1.clamp)(newVolume / 100, LOWEST_VOLUME, HIGHEST_VOLUME);
        }
        this.playerEngine.setVolume(volume);
    }
    getVolume() {
        var _a;
        return (_a = this.playerEngine.getVolume()) !== null && _a !== void 0 ? _a : 0;
    }
    toggleMuted() {
        this.playerEngine.toggleMuted();
    }
    setMuted(muted) {
        this.playerEngine.setMuted(muted);
    }
    isPlaying() {
        var _a;
        return (_a = this.playerEngine.isPlaying()) !== null && _a !== void 0 ? _a : false;
    }
    getCurrentTime() {
        var _a;
        return (_a = this.playerEngine.getCurrentTime()) !== null && _a !== void 0 ? _a : 0;
    }
    getDuration() {
        var _a;
        return (_a = this.playerEngine.getDuration()) !== null && _a !== void 0 ? _a : 0;
    }
    getSeekable() {
        var _a;
        return (_a = this.playerEngine.getSeekable()) !== null && _a !== void 0 ? _a : { start: 0, end: 0 };
    }
    getUTCCurrentTime() {
        var _a;
        return (_a = this.playerEngine.getUTCCurrentTime()) !== null && _a !== void 0 ? _a : 0;
    }
    getUTCDuration() {
        var _a;
        return (_a = this.playerEngine.getUTCDuration()) !== null && _a !== void 0 ? _a : 0;
    }
    getUTCSeekable() {
        var _a;
        return (_a = this.playerEngine.getUTCSeekable()) !== null && _a !== void 0 ? _a : { start: 0, end: 0 };
    }
    getContentMarkers() {
        return this.getState().contentMarkers;
    }
    setQualityLevel(level) {
        this.playerEngine.setQualityLevel(level);
        this.setState({ qualityLevel: level });
    }
    getQualityLevels() {
        return this.playerEngine
            .getQualityLevels()
            .sort((a, b) => a.bandwidth - b.bandwidth);
    }
    getQualityLevel() {
        if (this.playerEngine) {
            return this.playerEngine.getQualityLevel();
        }
        return js_player_shared_1.AUTO_QUALITY_LEVEL_DEFINITION;
    }
    setAudioTrack(track) {
        if (this.playerEngine) {
            this.playerEngine.setAudioTrack(track);
            this.setState({ qualityLevels: this.getQualityLevels() });
        }
    }
    getAudioTrack() {
        if (this.playerEngine) {
            return this.playerEngine.getAudioTrack();
        }
    }
    getAudioTracks() {
        if (this.playerEngine) {
            return this.playerEngine.getAudioTracks();
        }
        return [];
    }
    setSubtitleTrack(track) {
        if (this.playerEngine) {
            this.playerEngine.setSubtitleTrack(track, true);
        }
    }
    getSubtitleTrack() {
        if (this.playerEngine) {
            return this.playerEngine.getSubtitleTrack();
        }
    }
    getSubtitleTracks() {
        if (this.playerEngine) {
            return this.playerEngine.getSubtitleTracks();
        }
        return [];
    }
    clickThrough() {
        if (this.isPlaying()) {
            this.pause();
        }
        else {
            this.play();
        }
    }
    setState(state, emit, force = false) {
        if (!this.isFakePaused || force) {
            // if we're changing the seekable state we must also update the engines client restrictions.
            if ("isSeekable" in state) {
                this.playerEngine.setClientRestrictions({
                    timeshiftEnabled: state.isSeekable,
                });
            }
            super.setState(state, emit);
        }
    }
    onEngineStateChange(playbackState) {
        if (!this.isFakePaused) {
            this.setState({ playbackState });
        }
    }
    onPlaying() {
        if (!this.state.hasStarted) {
            this.onPlay();
        }
        else {
            this.emit(js_player_shared_1.PlayerEvents.PLAYING, this.getDefaultPlayerEvent());
        }
    }
    onDrmUpdate(type) {
        this.emit(js_player_shared_1.PlayerEvents.DRM_UPDATE, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { type }));
    }
    onLoaded() {
        var _a;
        if (!this.state.hasStarted) {
            this.emit(js_player_shared_1.PlayerEvents.LOADED, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { startTime: this.startTime, playSessionId: (_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.playSessionId }));
            this.setState({
                currentTime: this.getCurrentTime(),
                duration: this.getDuration(),
                seekable: this.getSeekable(),
                utcCurrentTime: this.getUTCCurrentTime(),
                utcDuration: this.getUTCDuration(),
                utcSeekable: this.getUTCSeekable(),
                qualityLevel: this.getQualityLevel(),
                qualityLevels: this.getQualityLevels(),
                isLive: this.isLive(),
                isSeekable: this.isSeekable(),
            });
        }
    }
    onPlay() {
        var _a, _b;
        if (this.state.hasStarted) {
            if (this.isLive() && !this.state.isSeekable) {
                // force enable timeshiftEnabled temporarily to allow
                // seekToLive to function.
                this.playerEngine.setClientRestrictions({
                    timeshiftEnabled: true,
                });
                this.seekToLive();
            }
            this.emit(js_player_shared_1.PlayerEvents.RESUME, this.getDefaultPlayerEvent());
        }
        else {
            const qualityLevel = this.getQualityLevel();
            this.emit(js_player_shared_1.PlayerEvents.START, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { playSessionId: (_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.playSessionId, startTime: this.startTime, source: (_b = this.mediaLocator) !== null && _b !== void 0 ? _b : "", bitrate: qualityLevel.bandwidth, qualityLevel, qualityLevels: this.getQualityLevels() }));
            this.setState({
                hasStarted: true,
            });
        }
    }
    onPause() {
        if (!this.isFakePaused && this.state.hasStarted) {
            this.emit(js_player_shared_1.PlayerEvents.PAUSE, this.getDefaultPlayerEvent());
        }
    }
    onMetadataChanged(data) {
        this.emit(js_player_shared_1.PlayerEvents.METADATA_EVENT, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), data));
    }
    onTimeUpdate() {
        if (this.state.hasStarted) {
            this.emit(js_player_shared_1.PlayerEvents.TIME_UPDATE, this.getDefaultPlayerEvent());
            const seekable = this.getSeekable();
            this.setState({
                currentTime: this.getCurrentTime(),
                duration: this.getDuration(),
                seekable,
                utcCurrentTime: this.getUTCCurrentTime(),
                utcDuration: this.getUTCDuration(),
                utcSeekable: this.getUTCSeekable(),
                isMuted: this.videoElement.muted,
                isLive: this.playerEngine.isLive(),
                isSeekable: this.isSeekable(),
                isAtLiveEdge: !this.isSeekable() || this.playerEngine.isAtLiveEdge(),
            });
        }
    }
    onSeeking() {
        if (this.debouncedSeek) {
            // when debounceSeeking the seeking event has been manually dispatched
            this.debouncedSeek = false;
        }
        else {
            this.emit(js_player_shared_1.PlayerEvents.SEEKING, this.getDefaultPlayerEvent());
        }
        this.setState({
            currentTime: this.getCurrentTime(),
            utcCurrentTime: this.getUTCCurrentTime(),
        });
    }
    onSeekTimeChange(time) {
        this.emit(js_player_shared_1.PlayerEvents.SEEK_TIME_CHANGE, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { currentTime: time, utcCurrentTime: this.getUTCCurrentTime() }));
        this.setState({
            currentTime: time,
            utcCurrentTime: this.getUTCCurrentTime(),
        }, true, true);
    }
    onSeeked() {
        this.scrubChange = 0;
        this.emit(js_player_shared_1.PlayerEvents.SEEKED, this.getDefaultPlayerEvent());
        this.setState({
            currentTime: this.getCurrentTime(),
            utcCurrentTime: this.getUTCCurrentTime(),
        });
    }
    onBuffering() {
        if (this.state.hasStarted) {
            this.emit(js_player_shared_1.PlayerEvents.BUFFERING, this.getDefaultPlayerEvent());
            const bufferingEvents = (this.state.bufferingEvents || 0) + 1;
            this.setState({ bufferingEvents });
        }
    }
    onBuffered() {
        this.emit(js_player_shared_1.PlayerEvents.BUFFERED, this.getDefaultPlayerEvent());
    }
    onDroppedFrames(totalDroppedFrames) {
        this.emit(js_player_shared_1.PlayerEvents.DROPPED_FRAMES, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { droppedFrames: totalDroppedFrames }));
        this.setState({ droppedFrames: totalDroppedFrames });
    }
    onSubtitleChanged(data) {
        var _a, _b, _c;
        if (this.state.hasStarted) {
            this.emit(js_player_shared_1.PlayerEvents.SUBTITLE_CHANGED, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { track: data.track }));
        }
        this.setState({
            subtitleTrack: this.getSubtitleTrack(),
            subtitleTracks: this.getSubtitleTracks(),
        });
        // This check is needed so that we don't save a 'metadata' track as preferred subtitle/kind
        if (data.shouldUpdatePreferences &&
            (!((_a = data.track) === null || _a === void 0 ? void 0 : _a.kind) ||
                AbstractBaseEngine_1.SUPPORTED_TEXT_TRACK_KINDS.includes(data.track.kind))) {
            (0, preferences_1.setPreferences)({
                subtitle: {
                    language: (_b = data.track) === null || _b === void 0 ? void 0 : _b.language,
                    kind: (_c = data.track) === null || _c === void 0 ? void 0 : _c.kind,
                },
            });
        }
    }
    onSubtitleCueChanged(cues) {
        this.subtitleContainer.innerHTML = "";
        cues.forEach((cue) => {
            const node = document.createTextNode(cue.text);
            // cueContainer is for positioning
            const cueContainer = document.createElement("div");
            cueContainer.className = "redbee-player-subtitle-cue-container";
            // cueElement is for SDK customers applying custom styles
            const cueElement = document.createElement("span");
            cueElement.className = "redbee-player-subtitle-cue";
            cueContainer.appendChild(cueElement);
            this.positionCue(cue, cueContainer.style);
            if (node) {
                cueElement.appendChild(node);
            }
            this.subtitleContainer.appendChild(cueContainer);
        });
    }
    onAudioChanged(data) {
        var _a, _b;
        if (this.state.hasStarted) {
            this.emit(js_player_shared_1.PlayerEvents.AUDIO_CHANGED, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), data));
        }
        this.setState({
            audioTrack: data.track,
            audioTracks: this.getAudioTracks(),
        });
        (0, preferences_1.setPreferences)({
            audio: {
                language: (_a = data.track) === null || _a === void 0 ? void 0 : _a.language,
                kind: (_b = data.track) === null || _b === void 0 ? void 0 : _b.kind,
            },
        });
    }
    onTracksChanged({ audioTrack, subtitleTrack, audioTracks, subtitleTracks, }) {
        this.setState({
            audioTrack,
            subtitleTrack,
            subtitleTracks,
            audioTracks,
        });
    }
    onVolumeChanged({ volume, muted }) {
        this.emit(js_player_shared_1.PlayerEvents.VOLUME_CHANGE, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { volume,
            muted }));
        this.setState({ volume, isMuted: muted });
    }
    onBitrateChanged(data) {
        this.emit(js_player_shared_1.PlayerEvents.BITRATE_CHANGED, data);
    }
    onEnded() {
        this.setState({ isSeekable: this.isSeekable() });
        this.emit(js_player_shared_1.PlayerEvents.ENDED, this.getDefaultPlayerEvent());
    }
    onError(data) {
        if (this.destroyed) {
            return;
        }
        this.emit(js_player_shared_1.PlayerEvents.ERROR, data);
    }
    positionCue(cue, style) {
        // Cue positioning
        // References: https://w3c.github.io/webvtt/ and https://www.speechpad.com/captions/webvtt
        // Current implementation doesn't support "vertical", "lineAlign" and "positionAlign"
        const transforms = [];
        // Size is the width of the cue box
        if (cue.size && cue.size !== 100) {
            style.width = `${cue.size}%`;
        }
        // Align can be: start|left|center|middle|right|end and affects positioning
        if (["start", "left"].includes(cue.align)) {
            style.textAlign = "left";
            if (Number.isFinite(cue.position)) {
                style.left = `${cue.position}%`;
            }
        }
        else if (["right", "end"].includes(cue.align)) {
            style.textAlign = "right";
            if (Number.isFinite(cue.position)) {
                style.right = `${100 - cue.position}%`;
            }
            else {
                style.right = "0";
            }
        }
        else {
            Object.assign(style, {
                textAlign: "center",
                left: "0",
                right: "0",
            });
            // When centered "position" 0% means left, 100% means right and 50% middle
            if (Number.isFinite(cue.position)) {
                transforms.push(`translateX(${cue.position - 50}%)`);
            }
        }
        if (cue.line === "auto") {
            style.bottom = "0";
        }
        else if (Number.isFinite(cue.line)) {
            const line = cue.line;
            if (cue.snapToLines) {
                if (cue.line < 0) {
                    style.bottom = "-1em";
                }
                transforms.push(`translateY(${line * 100}%)`);
            }
            else {
                style.top = `${cue.line}%`;
            }
        }
        if (transforms.length) {
            style.transform = transforms.join(" ");
        }
    }
    getStreamInfo() {
        if (!this.mediaLocator || !this.playbackFormat)
            return;
        return {
            mediaLocator: this.mediaLocator,
            format: this.playbackFormat,
            hasDrm: !!this.license,
        };
    }
    destroy() {
        this.destroyed = true;
        this.stop();
        this.playerEngine.destroy();
        super.destroy();
    }
}
exports.BasePlayer = BasePlayer;
//# sourceMappingURL=BasePlayer.js.map